import { useApolloClient } from "@apollo/client/react"; import { useEffect, useMemo, useState, useCallback } from "react";

import toast from "react-hot-toast";
import {
    GET_VISITA,
    GET_VISITA_TELE_AE,
    GET_VISITA_TELE_AP,
    GET_VISITA_TELE_CP,
    GET_VISITA_PRESENCIAL_AE,
    GET_VISITA_PRESENCIAL_AP,
    GET_VISITA_PRESENCIAL_CPCD,
    adaptVisitaGeneral,
    adaptVisitaTeleEnfermeriaAE,
    adaptVisitaTeleEnfermeriaAP,
    adaptVisitaTeleEnfermeriaCP,
    adaptVisitaPresencialAE,
    adaptVisitaPresencialAP,
    adaptVisitaPresencialCPCD
} from
    "../../../data/graphQL/queries/MisVisitas/getVisita";
import { GET_VISITA_CP, adaptVisitaCP } from "../../../data/graphQL/queries/MisVisitas/getVisitaCP";
import GET_VISITA_CD, { adaptVisitaCD } from "../../../data/graphQL/queries/MisVisitas/getVisitaCD";

import { ID_TIPO_VISITA_TELEENFERMERIA, ID_TIPO_VISITA_PRESENCIAL } from "../components/MisVisitasCreationContent";
import {
    GET_VISITA_DATO_CLINICO,
    GET_VISITA_ENTERAL,
    GET_VISITA_PARENTERAL,
    GET_VISITA_DISFAGIA,
    GET_VISITA_NPID,
    GET_VISITA_ORAL,
    GET_VISITA_KETO,
    GET_VISITA_CEAO,
    GET_VISITA_CEAE,
    GET_VISITA_HE,
    adaptVisitaDatoClinicoModel,
    adaptVisitaEnteralModel,
    adaptVisitaParenteralModel,
    adaptVisitaDisfagiaModel,
    adaptVisitaNpidModel,
    adaptVisitaOralModel,
    adaptVisitaKetoModel,
    adaptVisitaCeaoModel,
    adaptVisitaCeaeModel,
    adaptVisitaHeModel,
    adaptVisitaDisfagiaFonoModel
} from
    "../../../data/graphQL/queries/MisVisitas/getVisitaDetalleMedico";
import { idTiposProfesionales } from "../constants/tiposProfesionales";

/**
 * Hook que carga una visita existente según tipoVisita + programa`.
 *
 * @param {Object} params
 * @param {boolean} params.isNew                - Si la visita es nueva
 * @param {number}  params.tipoVisita           - ID tipo visita (presencial/tele)
 * @param {"AE"|"AP"|"CP"} params.programa      - Programa
 * @param {number}  params.idVisita             - Id de la visita
 * @param {Function}params.setVisita            - Setter del contexto/useState de visita
 * @param {boolean} [params.toastOnLoad=true]   - Muestra toast al cargar/errores
 * @returns {{ loading: boolean, error: any, reload: Function }}
 */

export const ID_TIPO_PROFESIONAL_ENFERMERO = 2;

export const useLoadVisita = ({
    isNew,
    tipoVisita,
    programa,
    idVisita,
    setVisita,
    toastOnLoad = true,
    idEvaluacion,
    idTipoProfesional
}) => {
    const client = useApolloClient();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Mapa de queries + adapters por (tipoVisita, programa)
    const pickSpecific = useCallback((resolvedTipoVisita, programa) => {
        let tele, pres;

        if (idTipoProfesional === idTiposProfesionales.FONOAUDIOLOGO) {// es Fonoaudiologo

            const fono = {
                DI: { query: GET_VISITA_DISFAGIA, adapter: adaptVisitaDisfagiaFonoModel, key: "visitaDisfagia", extraVars: { idEvaluacion } },
                CD: { query: GET_VISITA_DISFAGIA, adapter: adaptVisitaDisfagiaFonoModel, key: "visitaDisfagia", extraVars: { idEvaluacion } },
                CP: { query: GET_VISITA_DISFAGIA, adapter: adaptVisitaDisfagiaFonoModel, key: "visitaDisfagia", extraVars: { idEvaluacion } }
            };
            tele = { ...fono };
            pres = { ...fono };
        } else if (idTipoProfesional === ID_TIPO_PROFESIONAL_ENFERMERO) {// es Enfermero 
            tele = {
                AE: { query: GET_VISITA_TELE_AE, adapter: adaptVisitaTeleEnfermeriaAE, key: "visitaTeleEnfermeriaAE" },
                SI: { query: GET_VISITA_TELE_AE, adapter: adaptVisitaTeleEnfermeriaAE, key: "visitaTeleEnfermeriaAE" },
                CEAE: { query: GET_VISITA_TELE_AE, adapter: adaptVisitaTeleEnfermeriaAE, key: "visitaTeleEnfermeriaAE" },
                AEIS: { query: GET_VISITA_TELE_AE, adapter: adaptVisitaTeleEnfermeriaAE, key: "visitaTeleEnfermeriaAE" },
                DI: { query: GET_VISITA_TELE_AE, adapter: adaptVisitaTeleEnfermeriaAE, key: "visitaTeleEnfermeriaAE" },
                AP: { query: GET_VISITA_TELE_AP, adapter: adaptVisitaTeleEnfermeriaAP, key: "visitaTeleEnfermeriaAP" },
                APIS: { query: GET_VISITA_TELE_AP, adapter: adaptVisitaTeleEnfermeriaAP, key: "visitaTeleEnfermeriaAP" },
                SIP: { query: GET_VISITA_TELE_AP, adapter: adaptVisitaTeleEnfermeriaAP, key: "visitaTeleEnfermeriaAP" },
                HE: { query: GET_VISITA_TELE_AP, adapter: adaptVisitaTeleEnfermeriaAP, key: "visitaTeleEnfermeriaAP" },
                NPID: { query: GET_VISITA_TELE_AP, adapter: adaptVisitaTeleEnfermeriaAP, key: "visitaTeleEnfermeriaAP" },
                CP: { query: GET_VISITA_TELE_CP, adapter: adaptVisitaTeleEnfermeriaCP, key: "visitaTeleEnfermeriaCP" },
                CD: { query: GET_VISITA_TELE_CP, adapter: adaptVisitaTeleEnfermeriaCP, key: "visitaTeleEnfermeriaCP" }
            };
            pres = {
                AE: { query: GET_VISITA_PRESENCIAL_AE, adapter: adaptVisitaPresencialAE, key: "visitaPresencialAE" },
                DI: { query: GET_VISITA_PRESENCIAL_AE, adapter: adaptVisitaPresencialAE, key: "visitaPresencialAE" },
                SI: { query: GET_VISITA_PRESENCIAL_AE, adapter: adaptVisitaPresencialAE, key: "visitaPresencialAE" },
                CEAE: { query: GET_VISITA_PRESENCIAL_AE, adapter: adaptVisitaPresencialAE, key: "visitaPresencialAE" },
                AEIS: { query: GET_VISITA_PRESENCIAL_AE, adapter: adaptVisitaPresencialAE, key: "visitaPresencialAE" },
                AP: { query: GET_VISITA_PRESENCIAL_AP, adapter: adaptVisitaPresencialAP, key: "visitaPresencialAP" },
                APIS: { query: GET_VISITA_PRESENCIAL_AP, adapter: adaptVisitaPresencialAP, key: "visitaPresencialAP" },
                SIP: { query: GET_VISITA_PRESENCIAL_AP, adapter: adaptVisitaPresencialAP, key: "visitaPresencialAP" },
                HE: { query: GET_VISITA_PRESENCIAL_AP, adapter: adaptVisitaPresencialAP, key: "visitaPresencialAP" },
                NPID: { query: GET_VISITA_PRESENCIAL_AP, adapter: adaptVisitaPresencialAP, key: "visitaPresencialAP" },
                CP: { query: GET_VISITA_PRESENCIAL_CPCD, adapter: adaptVisitaPresencialCPCD, key: "visitaPresencialCPCD" },
                CD: { query: GET_VISITA_PRESENCIAL_CPCD, adapter: adaptVisitaPresencialCPCD, key: "visitaPresencialCPCD" }
            };
        } else if (idTipoProfesional === idTiposProfesionales.ENFERMERO_GUARDIA) {// es Enfermero Guardia
            tele = {
                CP: { query: GET_VISITA_TELE_CP, adapter: adaptVisitaTeleEnfermeriaCP, key: "visitaTeleEnfermeriaCP" },
                CD: { query: GET_VISITA_TELE_CP, adapter: adaptVisitaTeleEnfermeriaCP, key: "visitaTeleEnfermeriaCP" }
            };
            pres = {
                CP: { query: GET_VISITA_PRESENCIAL_CPCD, adapter: adaptVisitaPresencialCPCD, key: "visitaPresencialCPCD" },
                CD: { query: GET_VISITA_PRESENCIAL_CPCD, adapter: adaptVisitaPresencialCPCD, key: "visitaPresencialCPCD" }
            };
        } else {
            const medico = {
                AE: { query: GET_VISITA_ENTERAL, adapter: adaptVisitaEnteralModel, key: "visitaEnteral", extraVars: { idEvaluacion } },
                AP: { query: GET_VISITA_PARENTERAL, adapter: adaptVisitaParenteralModel, key: "visitaParenteral", extraVars: { idEvaluacion } },
                DI: { query: GET_VISITA_DISFAGIA, adapter: adaptVisitaDisfagiaModel, key: "visitaDisfagia", extraVars: { idEvaluacion } },
                NPID: { query: GET_VISITA_NPID, adapter: adaptVisitaNpidModel, key: "visitaNpid", extraVars: { idEvaluacion } },
                AO: { query: GET_VISITA_ORAL, adapter: adaptVisitaOralModel, key: "visitaOral", extraVars: { idEvaluacion } },
                AOC: { query: GET_VISITA_ORAL, adapter: adaptVisitaOralModel, key: "visitaOral", extraVars: { idEvaluacion } },
                KETO: { query: GET_VISITA_KETO, adapter: adaptVisitaKetoModel, key: "visitaKeto", extraVars: { idEvaluacion } },
                CEAO: { query: GET_VISITA_CEAO, adapter: adaptVisitaCeaoModel, key: "visitaCeao", extraVars: { idEvaluacion } },
                CEAE: { query: GET_VISITA_CEAE, adapter: adaptVisitaCeaeModel, key: "visitaCeae", extraVars: { idEvaluacion } },
                HE: { query: GET_VISITA_HE, adapter: adaptVisitaHeModel, key: "visitaHe" },
                CD: { query: GET_VISITA_CD, adapter: adaptVisitaCD, key: "visitaCD", extraVars: { idEvaluacion } },
                CP: { query: GET_VISITA_CP, adapter: adaptVisitaCP, key: "visitaCP", extraVars: { idEvaluacion } },
            };

            tele = { ...medico };
            pres = { ...medico };
        }

        if (resolvedTipoVisita === ID_TIPO_VISITA_TELEENFERMERIA) return tele[programa] ?? null;
        if (resolvedTipoVisita === ID_TIPO_VISITA_PRESENCIAL) return pres[programa] ?? null;

        //Para Medicos, no difieren las pantallas entre virtual o presencial, se retorna "tele" pero es lo mismo
        if (idTipoProfesional === idTiposProfesionales.FONOAUDIOLOGO) return tele[programa] ?? null;
        if (idTipoProfesional !== ID_TIPO_PROFESIONAL_ENFERMERO) return tele[programa] ?? null;

        return null;
    }, []);

    const canLoadSpecific = useCallback((resolvedTipoVisita) => {
        if (idTipoProfesional === ID_TIPO_PROFESIONAL_ENFERMERO) {
            return !isNew && !!idVisita;
        }
        return true;
    }, [isNew, idVisita, idTipoProfesional]);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            if (!!idVisita) {
                // 1) Traer SIEMPRE la visita general
                const { data: dataGeneral } = await client.query({
                    query: GET_VISITA,
                    variables: { idVisita: Number(idVisita) },
                    fetchPolicy: "network-only"
                });

                const general = adaptVisitaGeneral(dataGeneral?.visita) ?? null;
                if (!general) {
                    // if (toastOnLoad) toast("No se encontraron datos generales de la visita.");
                    setLoading(false);
                }

                const adaptedGeneral = general;

                // Merge al estado
                setVisita((prev) => ({
                    ...prev,
                    ...adaptedGeneral,
                }));
            }

            if (idTipoProfesional !== ID_TIPO_PROFESIONAL_ENFERMERO && programa !== 'CD' && programa !== 'CP') {
                // Si NO es enfermero, usar GET_VISITA_DATO_CLINICO y su adaptador
                const { data: datosClinicos } = await client.query({
                    query: GET_VISITA_DATO_CLINICO,
                    variables: { idVisita: Number(idVisita) || 0, idEvaluacion: Number(idEvaluacion) },
                    fetchPolicy: "network-only"
                });

                const adaptedDatosClinicos = adaptVisitaDatoClinicoModel(datosClinicos?.visitaDatoClinico) ?? null;
                if (!adaptedDatosClinicos) {
                    // if (toastOnLoad) toast("No se encontraron datos clínicos de la visita.");
                    setLoading(false);
                }
                setVisita((prev) => ({ ...prev, ...adaptedDatosClinicos }));
            }

            if (canLoadSpecific(tipoVisita)) {
                const picked = pickSpecific(tipoVisita, programa);
                if (picked?.query) {
                    const variables = { idVisita: Number(idVisita) || 0, ...(picked.extraVars || {}) };
                    const { data } = await client.query({
                        query: picked.query,
                        variables,
                        fetchPolicy: "network-only"
                    });

                    const raw = data?.[picked.key] ?? null;
                    if (raw) {
                        const adaptedSpecific = picked.adapter(raw);
                        setVisita((prev) => ({ ...prev, ...adaptedSpecific }));
                    }
                }
            }
        } catch (err) {
            setError(err);
            console.log(err);

            if (toastOnLoad) toast.error("Error al cargar la visita.");
        } finally {
            setLoading(false);
        }
    }, [
        client,
        idVisita,
        tipoVisita,
        programa,
        setVisita,
        toastOnLoad,
        canLoadSpecific,
        pickSpecific]
    );

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    return { loading, error, reload: fetchData };
};